import { FormHelperText } from "@mui/material";
import { useFormikContext, withFormik } from "formik";
import { StatusCodes } from "http-status-codes";
import ReCAPTCHA from "react-google-recaptcha";
import { useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import * as yup from "yup";
import { CitySelect } from "../localization/CitySelect";
import { ProvinceSelect } from "../localization/ProvinceSelect";
import { useProfileRepository } from "../profile/useProfileRepository";
import { SectorSelect } from "../sector/SectorSelect";
import { OrButton } from "../shared_modules/ui/components/atoms/or-button/or-button";
import { OrCheckbox } from "../shared_modules/ui/components/atoms/or-checkbox/or-checkbox";
import { MdiEye, MdiEyeOff } from "../shared_modules/ui/components/atoms/or-icon";
import { OrInput } from "../shared_modules/ui/components/atoms/or-input/or-input";
import { OrToastContainer, showOrToast } from "../shared_modules/ui/components/atoms/or-toast/or-toast";
import "./auth-ui.css";
import { useRegistrationVerification } from "./registration-verification-context";

export function CompanyRegister() {
  const { createProfile } = useProfileRepository();
  const { setPendingVerificationEmail } = useRegistrationVerification();

  const navigate = useNavigate();
  const isCaptchaEnabled = process.env.NODE_ENV === "production";

  const validationsForm = {
    company: yup.string().required("El nombre de empresa es requerido"),
    name: yup.string().required("El nombre es requerido"),
    cifnif: yup.string().required("El CIF/NIF es requerido"),
    city: yup.string().required("La ciudad es requerida"),
    province: yup.string().required("La provincia es requerida"),
    sector: yup.string().required("El sector es requerido"),
    captcha: isCaptchaEnabled ? yup.string().required("El captcha es requerido") : yup.string(),
    email: yup.string().email("Escribe un email válido").required("El email es requerido"),
    password: yup
      .string()
      .min(8, "La contraseña es demasiado corta. debe tener un mínimo de 8 caracteres.")
      .required("La password es requerida"),
    confirmpassword: yup
      .string()
      .oneOf([yup.ref("password"), null], "Las contraseñas deben coincidir")
      .required("La password es requerida"),
    policy: yup.boolean().oneOf([true], "Debes aceptar los términos y condiciones").required(),
  };

  const FormView = (props) => {
    const { setFieldValue } = useFormikContext();

    const handleRecaptchaChange = (value) => {
      setFieldValue("captcha", value);
    };

    const { values, touched, errors, isSubmitting, handleChange, handleSubmit } = props;

    return (
      <form className="or-auth-form" onSubmit={handleSubmit}>
        <div className="or-auth-form__group">
          <OrInput
            id="company"
            inputType="text"
            label="Empresa *"
            name="company"
            onChange={handleChange}
            value={values.company}
            hasError={touched.company && Boolean(errors.company)}
            errorText={touched.company ? errors.company : ""}
          />
        </div>
        <div className="or-auth-form__group">
          <OrInput
            id="name"
            inputType="text"
            label="Nombre *"
            name="name"
            onChange={handleChange}
            value={values.name}
            hasError={touched.name && Boolean(errors.name)}
            errorText={touched.name ? errors.name : ""}
          />
        </div>
        <div className="or-auth-form__group">
          <OrInput
            id="cifnif"
            inputType="text"
            label="CIF/NIF *"
            name="cifnif"
            onChange={handleChange}
            value={values.cifnif}
            hasError={touched.cifnif && Boolean(errors.cifnif)}
            errorText={touched.cifnif ? errors.cifnif : ""}
          />
        </div>
        <ProvinceSelect touched={touched} errors={errors} value={values.province} />
        <CitySelect touched={touched} errors={errors} value={values.city} provinceSelected={values.province} />
        <SectorSelect touched={touched} errors={errors} value={values.sector} />
        <div className="or-auth-form__group">
          <OrInput
            onChange={handleChange}
            id="email"
            inputType="email"
            label="Email *"
            name="email"
            value={values.email}
            hasError={touched.email && Boolean(errors.email)}
            errorText={touched.email ? errors.email : ""}
          />
        </div>
        <div className="or-auth-form__group">
          <OrInput
            id="password"
            inputType={values.showPassword ? "text" : "password"}
            label="Contraseña *"
            name="password"
            onChange={handleChange}
            placeholder="********************"
            value={values.password}
            hasError={touched.password && Boolean(errors.password)}
            errorText={touched.password ? errors.password : ""}
            rightSlot={
              <button
                className="or-auth-password-toggle"
                id="passwordicon"
                type="button"
                aria-label="toggle password visibility"
                onClick={() => setFieldValue("showPassword", !values.showPassword)}
              >
                {values.showPassword ? <MdiEyeOff /> : <MdiEye />}
              </button>
            }
          />
        </div>
        <div className="or-auth-form__group">
          <OrInput
            id="confirmpassword"
            inputType={values.showRepeatedPassword ? "text" : "password"}
            label="Repite la contraseña *"
            name="confirmpassword"
            onChange={handleChange}
            placeholder="********************"
            value={values.confirmpassword}
            hasError={touched.confirmpassword && Boolean(errors.confirmpassword)}
            errorText={touched.confirmpassword ? errors.confirmpassword : ""}
            rightSlot={
              <button
                className="or-auth-password-toggle"
                id="confirmpasswordicon"
                type="button"
                aria-label="toggle password visibility"
                onClick={() => setFieldValue("showRepeatedPassword", !values.showRepeatedPassword)}
              >
                {values.showRepeatedPassword ? <MdiEyeOff /> : <MdiEye />}
              </button>
            }
          />
        </div>
        {isCaptchaEnabled && (
          <div className="or-auth-form__group or-auth-form__group--terms">
            <div className="or-auth-form__recaptcha">
              <ReCAPTCHA sitekey={process.env.RECAPTCHA_SITE_KEY} onChange={handleRecaptchaChange} />
            </div>
            {touched.captcha && Boolean(errors.captcha) && <FormHelperText error>{errors.captcha}</FormHelperText>}
          </div>
        )}
        <div className="or-auth-form__group or-auth-form__group--terms">
          <OrCheckbox
            checked={Boolean(values.policy)}
            className="or-auth-checkbox"
            error={touched.policy && Boolean(errors.policy) ? errors.policy : undefined}
            id="policy"
            label={
              <>
                Acepto los{" "}
                <a
                  href="https://openreferences.com/aviso-legal/"
                  target="_blank"
                  className="or-auth-policy-link"
                  rel="noreferrer"
                >
                  terminos y condiciones
                </a>
                , y{" "}
                <a
                  className="or-auth-policy-link"
                  target="_blank"
                  href="https://openreferences.com/politica-de-privacidad/"
                  rel="noreferrer"
                >
                  política de privacidad
                </a>
              </>
            }
            name="policy"
            onChange={handleChange}
          />
        </div>
        <div className="or-auth-form__group">
          <OrButton type="submit" fullWidth isLoading={isSubmitting} size="lg" variant="primary">
            Regístrarme
          </OrButton>
        </div>
      </form>
    );
  };

  const Form = withFormik({
    mapPropsToValues: ({
      company,
      name,
      cifnif,
      city,
      province,
      sector,
      email,
      password,
      confirmpassword,
      showPassword,
      showRepeatedPassword,
      policy,
      captcha,
    }) => {
      return {
        company: company || "",
        name: name || "",
        cifnif: cifnif || "",
        city: city || "",
        province: province || "",
        sector: sector || "",
        email: email || "",
        password: password || "",
        confirmpassword: confirmpassword || "",
        showPassword: showPassword || false,
        showRepeatedPassword: showRepeatedPassword || false,
        policy: policy || false,
        captcha: captcha || "",
      };
    },
    enableReinitialize: true,
    validationSchema: yup.object().shape(validationsForm),

    handleSubmit: async (values, { setSubmitting }) => {
      try {
        let response = await createProfile({
          id: uuidv4(),
          name: values.name,
          cifnif: values.cifnif,
          city: values.city,
          province: values.province,
          sector: values.sector,
          companyName: values.company,
          username: values.email,
          password: values.password,
          captcha: isCaptchaEnabled ? values.captcha : "",
        });

        if (response.status !== StatusCodes.CREATED) {
          setSubmitting(false);
          return;
        }

        showOrToast("Usuario creado. Por favor, revisa tu bandeja de entrada para validar el correo");
        setPendingVerificationEmail(values.email);
        navigate("/");
      } catch (error) {
        showOrToast(error.response.data.message || "Error al crear la cuenta", { type: "error" });
      }
    },
  })(FormView);

  return (
    <>
      <OrToastContainer />
      <Form />
    </>
  );
}
